<template>
  <div class="action-details" v-if="action">
    <EntityPathBar
      type="action"
      :id="$route.params.id"
      :leafName="action?.name"
    />

    <div class="header">
      <h2>{{ isEditMode ? "Edit Activity" : action.name }}</h2>
      <div class="top-buttons">
        <button @click="toggleEditMode" class="icon-btn">
          <i class="bi bi-pencil"></i>
        </button>
        <button class="remove-btn" @click="deleteAction">
          <i class="bi bi-trash"></i>
        </button>
      </div>
    </div>

    <!-- Display mode -->
    <div v-if="!isEditMode">
      <div class="detail"><strong>Description:</strong> {{ action.description }}</div>
      <div class="detail"><strong>Status:</strong> {{ action.status }}</div>
      <div class="detail"><strong>Project:</strong> {{ projectName }}</div>
      <div class="detail"><strong>Workflow:</strong> {{ workflowName }}</div>
      <div class="detail"><strong>Location:</strong> {{ locationName }}</div>
    </div>

    <!-- Edit mode -->
    <div v-else>
      <div class="form-group">
        <label for="name">Name</label>
        <input type="text" id="name" v-model="editableAction.name">
      </div>
      <div class="form-group">
        <label for="description">Description</label>
        <input type="text" id="description" v-model="editableAction.description">
      </div>
      <div class="form-group">
        <label for="status">Status</label>
          <select id="status" v-model="editableAction.status" class="form-control">
            <option disabled value="">Status</option>
            <option value="contract">Contract</option>
            <option value="to be started">To be started</option>
            <option value="ongoing">Ongoing</option>
            <option value="finished">Finished</option>
          </select>
        </div>
      <div class="form-group">
        <label for="location">Location</label>
        <select id="location" v-model="editableAction.location_id" class="form-control">
          <option 
            v-for="loc in locations" 
            :key="loc.id" 
            :value="loc.id">
            {{ loc.name }}
          </option>
        </select>
      </div>
      <div class="buttons-container">
        <button @click="saveChanges">Save</button>
        <button @click="cancelEditMode">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import { API_ENDPOINTS } from '@/services/config';
import axios from 'axios';
import apiService from '@/services/apiService';
import { confirmDelete } from "@/utils/confirmDelete";
import { mapState } from 'vuex';
import EntityPathBar from './EntityPathBar.vue';

export default {
  props: {
    action: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    workflowName: {
      type: String,
      required: true,
    },
    projectID: {
      type: Number,
      required: true,
    },
    projectName: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      isEditMode: false,
      editableAction: {},
      locationName: "",
      locations: []
    };
  },
  mounted() {
    this.getLocationName();
    this.fetchLocations();
  },
  computed: {
    ...mapState(['loggedInUser']), 
  },
  components: {
    EntityPathBar
  },
  methods: {
    toggleEditMode() {
      this.isEditMode = true;
      // Create a deep copy of the action for editing
      this.editableAction = JSON.parse(JSON.stringify(this.action));
    },
    async saveChanges() {
      this.$emit('updateAction', this.editableAction); // Emit updated action to parent
      this.isEditMode = false;
    },
    cancelEditMode() {
      this.isEditMode = false;
    },
    async deleteAction() {
      const actionId = this.action.id;
      try {
        const isConfirmed = await confirmDelete("Are you sure you want to delete this Activity?");
        if (!isConfirmed) return; // Exit if the user cancels

        await apiService.deleteResource(API_ENDPOINTS.action(actionId), this.loggedInUser.id, this.loggedInUser.entra_id);
        this.$router.push({ name: 'WorkflowDetailPage', params: { id: this.action.workflow_id } }); // Redirect after deletion
      } catch (error) {
        console.error('Error deleting action:', error);
      }
    },
    async getLocationName() {
      try {
        const response = await apiService.get(API_ENDPOINTS.location(this.action.location_id));
        this.locationName = response.name;
      } catch (error) {
        console.error('Error fetching location name:', error);
      }
    },
    async fetchLocations() {
      try {
        const response = await axios.get(API_ENDPOINTS.locations);
        this.locations = response.data;
        console.log(response.data);
        console.log(this.locations);
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    }
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.action-details {
  max-width: 800px;
  margin: 0 auto;
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .buttons-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: flex-end;

    .left-buttons, .right-buttons {
      display: flex;
      gap: 10px;
    }
  }
  .top-buttons {
    display: flex;
    gap: 10px;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      color: $primary-color;
      margin-bottom: 20px;
    }
  }

  .detail {
    margin-bottom: 3px;
    font-size: 16px;
    strong {
      color: $primary-color;
    }

    pre {
      background-color: $form-color;
      padding: 10px;
      border-radius: 4px;
      overflow: auto;
      font-size: 14px;
      line-height: 1.6;
    }
  }
}
</style>
